import { ClientLog } from 'src/constants/clientLog';
import ClientLogger from './logger';

/**
 * Logs latency metrics to CloudWatch Logs for metric filter extraction.
 * To reduce CloudWatch ingestion costs, logging is sampled:
 * - Always logs when there's an error
 * - Always logs when duration exceeds the threshold (slow requests)
 * - For fast successful requests, logs are randomly sampled at SAMPLE_RATE
 *
 * The sampleRate field in the log payload enables inverse-probability weighting
 * to reconstruct an unbiased latency distribution from sampled data.
 */

// Threshold in milliseconds - log requests slower than this
const LATENCY_THRESHOLD_MS = 1000;

// Sample rate for fast/successful requests - log 1 in SAMPLE_RATE requests
const SAMPLE_RATE = 100;

export const logAPIRequestLatency = (endpoint: string, duration: number, isError: boolean = false): void => {
  // Validate duration - guard against NaN, non-finite, and negative values
  // These can occur when performance.now() is mocked or unavailable
  if (!Number.isFinite(duration) || duration < 0) {
    return;
  }

  const isSlow = duration >= LATENCY_THRESHOLD_MS;
  const isRandomlySampled = Math.random() < 1 / SAMPLE_RATE;

  // Log if there's an error, if the request is slow, or if randomly sampled
  if (!isError && !isSlow && !isRandomlySampled) {
    return;
  }

  // sampleRate indicates the sampling weight for this log entry:
  // - 1 for errors and slow requests (always logged)
  // - SAMPLE_RATE for randomly sampled fast/successful requests
  // This enables inverse-probability weighting for unbiased metric reconstruction
  const sampleRate = !isError && !isSlow ? SAMPLE_RATE : 1;

  ClientLogger.info(ClientLog.APIRequestLatency, {
    endpoint,
    duration,
    isError,
    sampleRate,
    timestamp: Date.now(),
  });
};
